import type { IAccessRangeForRole } from 'types/accessRangeForRole';

export const usersRoles = ['admin', 'user', 'guest', 'consultant'] as const;
export type AccessAccountRole = (typeof usersRoles)[number];

/** Kolejność zgodna z nagłówkami tabeli i AccessRangeForRole::AREA_SCOPES */
export const accessAreaScopesInTableOrder = [
  'other_plants_in_group',
  'whole_company_area_tree',
  'own_area_and_peers',
  'own_area_and_descendants'
] as const;

export type AccessAreaScope = (typeof accessAreaScopesInTableOrder)[number];

/** Klucze tłumaczeń dla kolumn zakresów (jak w nagłówkach). */
export const accessScopeLabelKeys = {
  other_plants_in_group: 'administration.otherBookmarksInGroup',
  whole_company_area_tree: 'administration.holeCompanyAreasTree',
  own_area_and_peers: 'administration.mineAndEqualAreas',
  own_area_and_descendants: 'administration.mineAndLevelsBelowAreas'
} as const satisfies Record<AccessAreaScope, string>;

export function mergeServerAccessRanges(
  apiRows: IAccessRangeForRole[]
): {
  ids: Partial<Record<AccessAccountRole, string>>;
  view: Record<AccessAccountRole, AccessAreaScope>;
  edit: Record<AccessAccountRole, AccessAreaScope>;
} | null {
  const byRole = Object.fromEntries(
    apiRows.map((r) => [r.account_role, r])
  ) as Partial<Record<AccessAccountRole, IAccessRangeForRole>>;

  for (const role of usersRoles) {
    const row = byRole[role];
    if (
      row == null ||
      row.view_area_scope == null ||
      row.edit_area_scope == null ||
      String(row.view_area_scope) === '' ||
      String(row.edit_area_scope) === ''
    )
      return null;
  }

  const ids: Partial<Record<AccessAccountRole, string>> = {};
  const view = {} as Record<AccessAccountRole, AccessAreaScope>;
  const edit = {} as Record<AccessAccountRole, AccessAreaScope>;

  for (const role of usersRoles) {
    const row = byRole[role] as IAccessRangeForRole;
    const rid = row.id;
    ids[role] = rid == null || rid === '' ? undefined : String(rid);
    view[role] = row.view_area_scope as AccessAreaScope;
    edit[role] = row.edit_area_scope as AccessAreaScope;
  }

  return { ids, view, edit };
}

export const eyesEmoji = 128064;
export const nibEmoji = 10002;
export const diskEmoji = 128190;
export const handEmoji = 9997;
export const crossEmoji = 10060;

export const functionalUserRights = [
  {
    name: 'administration.creating assessments, audits',
    guest: eyesEmoji,
    user: nibEmoji,
    admin: eyesEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.adding data (comments, photos) to existing ratings, audits',
    guest: nibEmoji,
    user: nibEmoji,
    admin: eyesEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.diagrams',
    guest: eyesEmoji,
    user: nibEmoji,
    admin: eyesEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.plans, reports',
    guest: eyesEmoji,
    user: nibEmoji,
    admin: eyesEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.notifications',
    guest: nibEmoji,
    user: nibEmoji,
    admin: nibEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.access to documents, templates, examples',
    guest: eyesEmoji,
    user: diskEmoji,
    admin: eyesEmoji,
    consultant: diskEmoji
  },
  {
    name: 'administration.adding your own documents, templates, examples',
    guest: crossEmoji,
    user: nibEmoji,
    admin: crossEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.changes in the structure of the company',
    guest: crossEmoji,
    user: nibEmoji,
    admin: nibEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.access to the administration panel',
    guest: crossEmoji,
    user: handEmoji,
    admin: nibEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.adding, blocking, unblocking users',
    guest: crossEmoji,
    user: crossEmoji,
    admin: nibEmoji,
    consultant: nibEmoji
  },
  {
    name: 'administration.making changes to the settings',
    guest: crossEmoji,
    user: crossEmoji,
    admin: nibEmoji,
    consultant: nibEmoji
  }
];
